import type { Pool } from "pg";

import {
  isKodiakPcbaCoreFeature,
  isKodiakTlaComponentSku,
  kodiakCoreFeatureFromBuildOrderSku,
  kodiakCoreFeatureValidationError,
  SQL_BUILD_ORDER_NOT_CANCELLED,
} from "./kodiak-pcba-core-feature.js";
import { sqlPoDisplayNumberExpr } from "./purchase-order-display.js";

export interface KodiakPlannedPrefix {
  pcba_prefix: string;
  core_feature: string;
  expected_tla_sku: string;
  purchase_order_id: number | null;
  pcba_build_order_id: number | null;
  tla_build_order_id: number | null;
  tla_work_order_number: string | null;
  po_number: string | null;
  notes: string | null;
  allowed_tla_skus: string[] | null;
}

export interface KodiakPcbaPrefixTlaExpectation {
  pcba_prefix: string;
  core_feature: string;
  expected_tla_sku: string;
  tla_build_order_id: number | null;
  tla_work_order_number: string | null;
  source: "planned" | "serial_exception";
  notes: string | null;
  allowed_tla_skus: string[] | null;
}

export type UpsertKodiakPlannedPrefixResult =
  | {
      ok: true;
      pcba_prefix: string;
      core_feature: string;
      expected_tla_sku: string;
      tla_build_order_id: number | null;
    }
  | { ok: false; status: number; error: string };

function parsePgTextArray(value: unknown): string[] | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const items = value.map((entry) => String(entry ?? "").trim()).filter((entry) => entry !== "");
    return items.length === 0 ? null : items;
  }
  return null;
}

export function kodiakPlannedLockDisplayLabel(expectation: {
  expected_tla_sku: string;
  allowed_tla_skus: string[] | null;
  tla_work_order_number?: string | null;
}): string {
  const allowed = (expectation.allowed_tla_skus ?? []).filter((sku) => sku.trim() !== "");
  if (allowed.length > 1) {
    const labels = [...new Set(allowed.map((sku) => kodiakTlaSkuShortLabel(sku)))].sort();
    return labels.join(" or ");
  }
  if (
    expectation.tla_work_order_number != null &&
    expectation.tla_work_order_number.trim() !== ""
  ) {
    return `${expectation.tla_work_order_number} · ${kodiakTlaSkuShortLabel(expectation.expected_tla_sku)}`;
  }
  return kodiakTlaSkuShortLabel(expectation.expected_tla_sku);
}

/** N, B, Q, W, or ? from a TLA component SKU. */
export function kodiakCoreFeatureFromTlaSku(tlaSku: string): string {
  return kodiakCoreFeatureFromBuildOrderSku(tlaSku) ?? "?";
}

/** Strip ALT- and uppercase for TLA SKU comparisons. */
export function normalizeKodiakTlaSkuForCompare(tlaSku: string): string {
  return tlaSku.trim().replace(/^ALT-/i, "").toUpperCase();
}

/**
 * Peel BABA-/ABB- so short-label compression does not collapse converted TLAs
 * onto their source SKUs (Configure still requires rebuild when the prefix is
 * only locked to the base TLA).
 */
function peelTlaConversionPrefix(tlaSku: string): { conversionPrefix: string; remainder: string } {
  const trimmed = tlaSku.trim();
  const babaMatch = trimmed.match(/^(BABA-)/i);
  if (babaMatch?.[1] != null) {
    return { conversionPrefix: "BABA-", remainder: trimmed.slice(babaMatch[1].length) };
  }
  const abbMatch = trimmed.match(/^(ABB-)/i);
  if (abbMatch?.[1] != null) {
    return { conversionPrefix: "ABB-", remainder: trimmed.slice(abbMatch[1].length) };
  }
  return { conversionPrefix: "", remainder: trimmed };
}

/**
 * Short TLA label. FI-3C keeps the clamp letter (N-TLA-A). FI-5A has no clamp
 * designator (N-TLA, W-TLA). BABA/ABB prefixes are retained (BABA-W-TLA).
 */
export function kodiakTlaSkuShortLabel(tlaSku: string): string {
  const { conversionPrefix, remainder } = peelTlaConversionPrefix(tlaSku);
  const normalized = normalizeKodiakTlaSkuForCompare(remainder);
  const fi5a = normalized.match(/FI-5A-([NW])-TLA/);
  if (fi5a?.[1] != null) {
    return `${conversionPrefix}${fi5a[1]}-TLA`;
  }
  const fi3c = normalized.match(/-([NBQ])-TLA-([AC])/);
  if (fi3c?.[1] != null && fi3c[2] != null) {
    return `${conversionPrefix}${fi3c[1]}-TLA-${fi3c[2]}`;
  }
  return conversionPrefix === "" ? normalized : `${conversionPrefix}${normalized}`;
}

export function kodiakTlaSkusEquivalent(left: string, right: string): boolean {
  return kodiakTlaSkuShortLabel(left) === kodiakTlaSkuShortLabel(right);
}

/** Five-digit Kodiak PCBA prefix from a hyphenated or compact PCBA serial, or null. */
export function parseKodiakPcbaPrefixFromSerial(pcbaSerial: string): string | null {
  const trimmed = pcbaSerial.trim();
  const hyphenatedMatch = trimmed.match(/^([0-9]{5})-/);
  if (hyphenatedMatch != null) {
    return hyphenatedMatch[1];
  }
  const compactMatch = trimmed.match(/^([0-9]{5})([0-9]{4,})$/);
  return compactMatch?.[1] ?? null;
}

/**
 * Postgres match for prefix counts: hyphenated `46550-0123`, or compact nine- or
 * ten-digit `466261981` / `4655200001`. Leading digit 4-9 leaves year-encoded
 * clamp serials and short numeric QC typos out of the prefix list.
 */
export const KODIAK_PCBA_SERIAL_HYPHENATED_SQL = "^[0-9]{5}-";
export const KODIAK_PCBA_SERIAL_COMPACT_SQL = "^[4-9][0-9]{8,9}$";

export function sqlKodiakPcbaSerialPredicate(columnSql: string): string {
  return `(${columnSql} ~ '${KODIAK_PCBA_SERIAL_HYPHENATED_SQL}' or ${columnSql} ~ '${KODIAK_PCBA_SERIAL_COMPACT_SQL}')`;
}

/** Suntronic CM PCBA barcode (for example SD-8211-2126-00090). */
const SUNTRONIC_PCBA_SERIAL_RE = /^SD-\d{4}-\d{4}-\d+$/i;

export function isSuntronicPcbaSerial(pcbaSerial: string): boolean {
  return SUNTRONIC_PCBA_SERIAL_RE.test(pcbaSerial.trim());
}

/** Kodiak five-digit prefix serials and Suntronic SD-… PCBA barcodes. */
export function isRecognizedAssemblyPcbaSerial(pcbaSerial: string): boolean {
  const trimmed = pcbaSerial.trim();
  if (trimmed === "") return false;
  return parseKodiakPcbaPrefixFromSerial(trimmed) != null || isSuntronicPcbaSerial(trimmed);
}

const SQL_LOAD_PLANNED = `
select mrp.kodiak_pcba_prefix_planned.pcba_prefix,
       mrp.kodiak_pcba_prefix_planned.core_feature,
       mrp.kodiak_pcba_prefix_planned.expected_tla_sku,
       mrp.kodiak_pcba_prefix_planned.purchase_order_id,
       mrp.kodiak_pcba_prefix_planned.pcba_build_order_id,
       mrp.kodiak_pcba_prefix_planned.tla_build_order_id,
       mrp.kodiak_pcba_prefix_planned.notes,
       mrp.kodiak_pcba_prefix_planned.allowed_tla_skus,
       tla_build_order.number as tla_work_order_number,
       (${sqlPoDisplayNumberExpr("purchase_order")}) as po_number
  from mrp.kodiak_pcba_prefix_planned
  left join public.build_order tla_build_order
    on tla_build_order.id = mrp.kodiak_pcba_prefix_planned.tla_build_order_id
  left join public.purchase_order purchase_order
    on purchase_order.id = mrp.kodiak_pcba_prefix_planned.purchase_order_id
 order by mrp.kodiak_pcba_prefix_planned.pcba_prefix,
          mrp.kodiak_pcba_prefix_planned.core_feature
`;

interface KodiakTlaBuildOrderForPlannedPrefix {
  tla_build_order_id: number;
  expected_tla_sku: string;
  purchase_order_id: number;
  tla_work_order_number: string;
  quantity: number;
}

async function loadKodiakTlaBuildOrderForPlannedPrefix(
  pool: Pool,
  tlaBuildOrderId: number,
): Promise<KodiakTlaBuildOrderForPlannedPrefix | null> {
  const result = await pool.query<Record<string, unknown>>(
    `select public.build_order.id as tla_build_order_id,
            public.build_order.sku as expected_tla_sku,
            public.build_order.number as tla_work_order_number,
            public.build_order.quantity,
            public.purchase_order.id as purchase_order_id
       from public.build_order
       join public.purchase_order_item
         on public.purchase_order_item.id = public.build_order.purchase_order_item_id
       join public.purchase_order
         on public.purchase_order.id = public.purchase_order_item.purchase_order_id
      where public.build_order.id = $1
        and public.build_order.location_id = 14
        and public.build_order.sku ~ 'TLA'
        and ${SQL_BUILD_ORDER_NOT_CANCELLED}`,
    [tlaBuildOrderId],
  );
  const row = result.rows[0];
  if (row == null) return null;
  return {
    tla_build_order_id: Number(row.tla_build_order_id),
    expected_tla_sku: String(row.expected_tla_sku ?? ""),
    purchase_order_id: Number(row.purchase_order_id),
    tla_work_order_number: String(row.tla_work_order_number ?? ""),
    quantity: Math.max(1, Math.round(Number(row.quantity) || 0)),
  };
}

async function writeKodiakPrefixTlaSlice(
  pool: Pool,
  input: {
    pcba_prefix: string;
    core_feature: string;
    quantity: number;
    expected_tla_sku: string;
    purchase_order_id: number | null;
    pcba_build_order_id: number | null;
    tla_build_order_id: number;
    updated_by: string;
  },
): Promise<void> {
  const childCm = await pool.query<{ id: string }>(
    `select manufacturing.cm_purchase_order.id
       from manufacturing.cm_purchase_order
       join manufacturing.cm_purchase_order_work_order
         on manufacturing.cm_purchase_order_work_order.cm_purchase_order_id =
            manufacturing.cm_purchase_order.id
      where manufacturing.cm_purchase_order_work_order.public_build_order_id = $1
      order by manufacturing.cm_purchase_order.id
      limit 1`,
    [input.tla_build_order_id],
  );
  const childCmPurchaseOrderId =
    childCm.rows[0] == null ? null : Number(childCm.rows[0].id);

  await pool.query(
    `insert into mrp.kodiak_pcba_prefix_tla_slice (
       pcba_prefix,
       core_feature,
       quantity,
       expected_tla_sku,
       purchase_order_id,
       pcba_build_order_id,
       tla_build_order_id,
       child_cm_purchase_order_id,
       updated_by
     ) values ($1, $2, $3, $4, $5, $6, $7, $8, $9)
     on conflict (pcba_prefix, core_feature, tla_build_order_id) do update
       set quantity = excluded.quantity,
           expected_tla_sku = excluded.expected_tla_sku,
           purchase_order_id = excluded.purchase_order_id,
           pcba_build_order_id = excluded.pcba_build_order_id,
           child_cm_purchase_order_id = excluded.child_cm_purchase_order_id,
           updated_by = excluded.updated_by,
           updated_at = now()`,
    [
      input.pcba_prefix,
      input.core_feature,
      input.quantity,
      input.expected_tla_sku,
      input.purchase_order_id,
      input.pcba_build_order_id,
      input.tla_build_order_id,
      childCmPurchaseOrderId,
      input.updated_by,
    ],
  );
}

export async function loadKodiakPlannedPrefixes(pool: Pool): Promise<KodiakPlannedPrefix[]> {
  const result = await pool.query<Record<string, unknown>>(SQL_LOAD_PLANNED);
  return result.rows.map((row) => ({
    pcba_prefix: String(row.pcba_prefix ?? ""),
    core_feature: String(row.core_feature ?? ""),
    expected_tla_sku: String(row.expected_tla_sku ?? ""),
    purchase_order_id:
      row.purchase_order_id == null ? null : Number(row.purchase_order_id),
    pcba_build_order_id:
      row.pcba_build_order_id == null ? null : Number(row.pcba_build_order_id),
    tla_build_order_id:
      row.tla_build_order_id == null ? null : Number(row.tla_build_order_id),
    tla_work_order_number:
      row.tla_work_order_number == null || String(row.tla_work_order_number).trim() === ""
        ? null
        : String(row.tla_work_order_number),
    po_number:
      row.po_number == null || String(row.po_number).trim() === "" ? null : String(row.po_number),
    notes: row.notes == null || String(row.notes).trim() === "" ? null : String(row.notes),
    allowed_tla_skus: parsePgTextArray(row.allowed_tla_skus),
  }));
}

interface KodiakTlaLineOnPcbaBuildOrder {
  tla_build_order_id: number;
  tla_sku: string;
  tla_work_order_number: string;
}

export async function loadTlaSkusForPcbaBuildOrder(
  pool: Pool,
  pcbaBuildOrderId: number,
): Promise<KodiakTlaLineOnPcbaBuildOrder[]> {
  const result = await pool.query<Record<string, unknown>>(
    `select public.build_order.id as tla_build_order_id,
            public.build_order.sku as tla_sku,
            public.build_order.number as tla_work_order_number
       from public.build_order pcba_build_order
       join public.purchase_order_item pcba_purchase_order_item
         on pcba_purchase_order_item.id = pcba_build_order.purchase_order_item_id
       join public.purchase_order
         on public.purchase_order.id = pcba_purchase_order_item.purchase_order_id
       join public.purchase_order_item tla_purchase_order_item
         on tla_purchase_order_item.purchase_order_id = public.purchase_order.id
       join public.build_order
         on public.build_order.purchase_order_item_id = tla_purchase_order_item.id
      where pcba_build_order.id = $1
        and pcba_build_order.location_id = 14
        and public.build_order.location_id = 14
        and public.build_order.sku ~* 'TLA'
        and ${SQL_BUILD_ORDER_NOT_CANCELLED}
        and (
          pcba_build_order.sku !~ '^15-1607-([NBQ])-'
          or public.build_order.sku ~ (
            '-'
            || substring(pcba_build_order.sku from '^15-1607-([NBQ])-')
            || '-TLA-'
          )
        )
      union
      select public.build_order.id as tla_build_order_id,
             public.build_order.sku as tla_sku,
             public.build_order.number as tla_work_order_number
        from public.build_order pcba_build_order
        join public.purchase_order_item pcba_purchase_order_item
          on pcba_purchase_order_item.id = pcba_build_order.purchase_order_item_id
        join manufacturing.cm_purchase_order parent_cm
          on parent_cm.public_purchase_order_id = pcba_purchase_order_item.purchase_order_id
        join manufacturing.cm_purchase_order child_cm
          on child_cm.parent_cm_purchase_order_id = parent_cm.id
        join manufacturing.cm_purchase_order_work_order child_link
          on child_link.cm_purchase_order_id = child_cm.id
        join public.build_order
          on public.build_order.id = child_link.public_build_order_id
       where pcba_build_order.id = $1
         and pcba_build_order.location_id = 14
         and public.build_order.location_id = 14
         and public.build_order.sku ~* 'TLA'
         and ${SQL_BUILD_ORDER_NOT_CANCELLED}
       order by tla_work_order_number`,
    [pcbaBuildOrderId],
  );
  return result.rows.map((row) => ({
    tla_build_order_id: Number(row.tla_build_order_id),
    tla_sku: String(row.tla_sku ?? ""),
    tla_work_order_number: String(row.tla_work_order_number ?? ""),
  }));
}

async function writeKodiakPlannedPrefixRow(
  pool: Pool,
  input: {
    pcba_prefix: string;
    core_feature: string;
    expected_tla_sku: string;
    purchase_order_id: number | null;
    pcba_build_order_id: number | null;
    tla_build_order_id: number | null;
    allowed_tla_skus: string[] | null;
    notes: string | null;
    updated_by: string;
  },
): Promise<void> {
  await pool.query(
    `insert into mrp.kodiak_pcba_prefix_planned (
       pcba_prefix,
       core_feature,
       expected_tla_sku,
       purchase_order_id,
       pcba_build_order_id,
       tla_build_order_id,
       allowed_tla_skus,
       notes,
       updated_by
     ) values ($1, $2, $3, $4, $5, $6, $7, $8, $9)
     on conflict (pcba_prefix, core_feature) do update
       set expected_tla_sku = excluded.expected_tla_sku,
           purchase_order_id = excluded.purchase_order_id,
           pcba_build_order_id = excluded.pcba_build_order_id,
           tla_build_order_id = excluded.tla_build_order_id,
           allowed_tla_skus = excluded.allowed_tla_skus,
           notes = excluded.notes,
           updated_by = excluded.updated_by,
           updated_at = now()`,
    [
      input.pcba_prefix,
      input.core_feature,
      input.expected_tla_sku,
      input.purchase_order_id,
      input.pcba_build_order_id,
      input.tla_build_order_id,
      input.allowed_tla_skus,
      input.notes,
      input.updated_by,
    ],
  );
}

export async function upsertKodiakPlannedLockForAssignment(
  pool: Pool,
  input: {
    pcba_prefix: string;
    core_feature: string;
    purchase_order_id: number;
    pcba_build_order_id: number;
    tla_build_order_id?: number | null;
    updated_by: string;
  },
): Promise<UpsertKodiakPlannedPrefixResult> {
  const pcbaPrefix = input.pcba_prefix.trim();
  const coreFeature = input.core_feature.trim().toUpperCase();
  const purchaseOrderId = Number(input.purchase_order_id);
  const pcbaBuildOrderId = Number(input.pcba_build_order_id);
  const specificTlaBuildOrderId =
    input.tla_build_order_id == null ? null : Number(input.tla_build_order_id);

  if (!/^[0-9]{5}$/.test(pcbaPrefix)) {
    return { ok: false, status: 400, error: "pcba_prefix must be exactly five digits." };
  }
  if (!isKodiakPcbaCoreFeature(coreFeature)) {
    return { ok: false, status: 400, error: kodiakCoreFeatureValidationError() };
  }

  if (specificTlaBuildOrderId != null && specificTlaBuildOrderId > 0) {
    const tlaBuildOrder = await loadKodiakTlaBuildOrderForPlannedPrefix(pool, specificTlaBuildOrderId);
    if (tlaBuildOrder == null) {
      return {
        ok: false,
        status: 400,
        error: "tla_build_order_id must reference an active Kodiak TLA build order.",
      };
    }
    const expectedTlaSku = tlaBuildOrder.expected_tla_sku.trim();
    await writeKodiakPlannedPrefixRow(pool, {
      pcba_prefix: pcbaPrefix,
      core_feature: coreFeature,
      expected_tla_sku: expectedTlaSku,
      purchase_order_id: purchaseOrderId,
      pcba_build_order_id: pcbaBuildOrderId,
      tla_build_order_id: specificTlaBuildOrderId,
      allowed_tla_skus: [expectedTlaSku],
      notes: null,
      updated_by: input.updated_by,
    });
    await writeKodiakPrefixTlaSlice(pool, {
      pcba_prefix: pcbaPrefix,
      core_feature: coreFeature,
      quantity: tlaBuildOrder.quantity,
      expected_tla_sku: expectedTlaSku,
      purchase_order_id: purchaseOrderId,
      pcba_build_order_id: pcbaBuildOrderId,
      tla_build_order_id: specificTlaBuildOrderId,
      updated_by: input.updated_by,
    });
    return {
      ok: true,
      pcba_prefix: pcbaPrefix,
      core_feature: coreFeature,
      expected_tla_sku: expectedTlaSku,
      tla_build_order_id: specificTlaBuildOrderId,
    };
  }

  const tlaLines = await loadTlaSkusForPcbaBuildOrder(pool, pcbaBuildOrderId);
  const allowedTlaSkus = [
    ...new Set(
      tlaLines
        .map((line) => line.tla_sku.trim())
        .filter((sku) => sku !== "" && isKodiakTlaComponentSku(sku)),
    ),
  ];
  if (allowedTlaSkus.length === 0) {
    return {
      ok: false,
      status: 400,
      error: "Could not resolve TLA lines for this PCBA build order.",
    };
  }

  const expectedTlaSku =
    allowedTlaSkus.length === 1
      ? (allowedTlaSkus[0] ?? `${coreFeature}-TLA-?`)
      : `${coreFeature}-TLA-?`;

  await writeKodiakPlannedPrefixRow(pool, {
    pcba_prefix: pcbaPrefix,
    core_feature: coreFeature,
    expected_tla_sku: expectedTlaSku,
    purchase_order_id: purchaseOrderId,
    pcba_build_order_id: pcbaBuildOrderId,
    tla_build_order_id: null,
    allowed_tla_skus: allowedTlaSkus,
    notes: null,
    updated_by: input.updated_by,
  });

  return {
    ok: true,
    pcba_prefix: pcbaPrefix,
    core_feature: coreFeature,
    expected_tla_sku: expectedTlaSku,
    tla_build_order_id: null,
  };
}

export async function syncMissingPlannedLocksFromAssignments(pool: Pool, updatedBy: string): Promise<void> {
  const assignmentRows = await pool.query<{
    pcba_prefix: string;
    core_feature: string;
    purchase_order_id: string | number;
    pcba_build_order_id: string | number;
    tla_build_order_id: string | number | null;
  }>(
    `select mrp.kodiak_pcba_prefix_assignment.pcba_prefix,
            mrp.kodiak_pcba_prefix_assignment.core_feature,
            mrp.kodiak_pcba_prefix_assignment.purchase_order_id,
            mrp.kodiak_pcba_prefix_assignment.pcba_build_order_id,
            mrp.kodiak_pcba_prefix_planned.tla_build_order_id
       from mrp.kodiak_pcba_prefix_assignment
       left join mrp.kodiak_pcba_prefix_planned
         on mrp.kodiak_pcba_prefix_planned.pcba_prefix = mrp.kodiak_pcba_prefix_assignment.pcba_prefix
        and mrp.kodiak_pcba_prefix_planned.core_feature = mrp.kodiak_pcba_prefix_assignment.core_feature`,
  );

  for (const row of assignmentRows.rows) {
    const specificTlaBuildOrderId =
      row.tla_build_order_id == null ? null : Number(row.tla_build_order_id);
    if (specificTlaBuildOrderId != null && specificTlaBuildOrderId > 0) {
      continue;
    }
    await upsertKodiakPlannedLockForAssignment(pool, {
      pcba_prefix: String(row.pcba_prefix),
      core_feature: String(row.core_feature),
      purchase_order_id: Number(row.purchase_order_id),
      pcba_build_order_id: Number(row.pcba_build_order_id),
      updated_by: updatedBy,
    });
  }
}

export async function upsertKodiakPlannedPrefix(
  pool: Pool,
  input: {
    pcba_prefix: string;
    core_feature?: string;
    expected_tla_sku?: string;
    purchase_order_id?: number | null;
    pcba_build_order_id?: number | null;
    tla_build_order_id: number | null;
    notes?: string | null;
    updated_by: string;
  },
): Promise<UpsertKodiakPlannedPrefixResult> {
  const pcbaPrefix = input.pcba_prefix.trim();
  const tlaBuildOrderId =
    input.tla_build_order_id == null ? null : Number(input.tla_build_order_id);

  if (!/^[0-9]{5}$/.test(pcbaPrefix)) {
    return { ok: false, status: 400, error: "pcba_prefix must be exactly five digits." };
  }
  if (tlaBuildOrderId == null || !Number.isInteger(tlaBuildOrderId) || tlaBuildOrderId <= 0) {
    return { ok: false, status: 400, error: "tla_build_order_id is required." };
  }

  const tlaBuildOrder = await loadKodiakTlaBuildOrderForPlannedPrefix(pool, tlaBuildOrderId);
  if (tlaBuildOrder == null) {
    return {
      ok: false,
      status: 400,
      error: "tla_build_order_id must reference an active Kodiak TLA build order.",
    };
  }
  const expectedTlaSku = tlaBuildOrder.expected_tla_sku.trim();
  if (expectedTlaSku === "" || !isKodiakTlaComponentSku(expectedTlaSku)) {
    return {
      ok: false,
      status: 400,
      error: "Selected TLA build order does not have a valid TLA component SKU.",
    };
  }
  const coreFeature = kodiakCoreFeatureFromTlaSku(expectedTlaSku);

  const purchaseOrderId =
    input.purchase_order_id == null ? tlaBuildOrder.purchase_order_id : Number(input.purchase_order_id);
  const pcbaBuildOrderId =
    input.pcba_build_order_id == null ? null : Number(input.pcba_build_order_id);
  const notes =
    input.notes == null || String(input.notes).trim() === "" ? null : String(input.notes).trim();

  if (purchaseOrderId != null && (!Number.isInteger(purchaseOrderId) || purchaseOrderId <= 0)) {
    return { ok: false, status: 400, error: "purchase_order_id must be a positive integer." };
  }
  if (pcbaBuildOrderId != null && (!Number.isInteger(pcbaBuildOrderId) || pcbaBuildOrderId <= 0)) {
    return { ok: false, status: 400, error: "pcba_build_order_id must be a positive integer." };
  }

  await writeKodiakPlannedPrefixRow(pool, {
    pcba_prefix: pcbaPrefix,
    core_feature: coreFeature,
    expected_tla_sku: expectedTlaSku,
    purchase_order_id: purchaseOrderId,
    pcba_build_order_id: pcbaBuildOrderId,
    tla_build_order_id: tlaBuildOrderId,
    allowed_tla_skus: [expectedTlaSku],
    notes,
    updated_by: input.updated_by,
  });

  return {
    ok: true,
    pcba_prefix: pcbaPrefix,
    core_feature: coreFeature,
    expected_tla_sku: expectedTlaSku,
    tla_build_order_id: tlaBuildOrderId,
  };
}

export async function deleteKodiakPlannedPrefix(
  pool: Pool,
  pcbaPrefix: string,
  coreFeature: string,
): Promise<{ ok: true } | { ok: false; status: number; error: string }> {
  const prefix = pcbaPrefix.trim();
  const core = coreFeature.trim().toUpperCase();
  if (!/^[0-9]{5}$/.test(prefix)) {
    return { ok: false, status: 400, error: "pcba_prefix must be exactly five digits." };
  }
  if (!isKodiakPcbaCoreFeature(core)) {
    return { ok: false, status: 400, error: kodiakCoreFeatureValidationError() };
  }
  await pool.query(
    `delete from mrp.kodiak_pcba_prefix_planned
      where pcba_prefix = $1
        and core_feature = $2`,
    [prefix, core],
  );
  return { ok: true };
}

function pickPlannedRowForRequiredTla(
  rows: KodiakPlannedPrefix[],
  requiredTlaSku: string | null | undefined,
): KodiakPlannedPrefix | null {
  if (rows.length === 0) return null;
  const required = requiredTlaSku == null ? "" : requiredTlaSku.trim();
  if (required !== "") {
    const matchingRequired = rows.find((row) => plannedRowMatchesRequiredTla(row, required));
    if (matchingRequired != null) return matchingRequired;
  }
  if (rows.length === 1) return rows[0] ?? null;
  return rows[0] ?? null;
}

function plannedRowMatchesRequiredTla(row: KodiakPlannedPrefix, requiredTlaSku: string): boolean {
  const allowed = row.allowed_tla_skus ?? [];
  if (allowed.length > 0) {
    return allowed.some((sku) => kodiakTlaSkusEquivalent(sku, requiredTlaSku));
  }
  if (row.expected_tla_sku.trim().endsWith("-TLA-?")) {
    const requiredCore = kodiakCoreFeatureFromTlaSku(requiredTlaSku);
    return requiredCore === row.core_feature;
  }
  return kodiakTlaSkusEquivalent(row.expected_tla_sku, requiredTlaSku);
}

export async function resolveKodiakPcbaPrefixTlaExpectation(
  pool: Pool,
  input: { pcba_serial: string; required_tla_sku?: string | null },
): Promise<KodiakPcbaPrefixTlaExpectation | null> {
  const pcbaSerial = input.pcba_serial.trim();
  const pcbaPrefix = parseKodiakPcbaPrefixFromSerial(pcbaSerial);
  if (pcbaPrefix == null) return null;

  const serialException = await pool.query<Record<string, unknown>>(
    `select mrp.kodiak_pcba_serial_tla_exception.expected_tla_sku,
            mrp.kodiak_pcba_serial_tla_exception.tla_build_order_id,
            mrp.kodiak_pcba_serial_tla_exception.notes,
            public.build_order.number as tla_work_order_number
       from mrp.kodiak_pcba_serial_tla_exception
       left join public.build_order
         on public.build_order.id = mrp.kodiak_pcba_serial_tla_exception.tla_build_order_id
      where lower(trim(mrp.kodiak_pcba_serial_tla_exception.pcba_serial)) = lower(trim($1::text))`,
    [pcbaSerial],
  );
  const exceptionRow = serialException.rows[0];
  if (exceptionRow != null) {
    const expectedTlaSku = String(exceptionRow.expected_tla_sku ?? "").trim();
    if (expectedTlaSku !== "") {
      return {
        pcba_prefix: pcbaPrefix,
        core_feature: kodiakCoreFeatureFromTlaSku(expectedTlaSku),
        expected_tla_sku: expectedTlaSku,
        tla_build_order_id:
          exceptionRow.tla_build_order_id == null ? null : Number(exceptionRow.tla_build_order_id),
        tla_work_order_number:
          exceptionRow.tla_work_order_number == null ||
          String(exceptionRow.tla_work_order_number).trim() === ""
            ? null
            : String(exceptionRow.tla_work_order_number),
        source: "serial_exception",
        notes:
          exceptionRow.notes == null || String(exceptionRow.notes).trim() === ""
            ? null
            : String(exceptionRow.notes),
        allowed_tla_skus: [expectedTlaSku],
      };
    }
  }

  const result = await pool.query<Record<string, unknown>>(
    `select mrp.kodiak_pcba_prefix_planned.pcba_prefix,
            mrp.kodiak_pcba_prefix_planned.core_feature,
            mrp.kodiak_pcba_prefix_planned.expected_tla_sku,
            mrp.kodiak_pcba_prefix_planned.tla_build_order_id,
            mrp.kodiak_pcba_prefix_planned.notes,
            mrp.kodiak_pcba_prefix_planned.allowed_tla_skus,
            tla_build_order.number as tla_work_order_number
       from mrp.kodiak_pcba_prefix_planned
       left join public.build_order tla_build_order
         on tla_build_order.id = mrp.kodiak_pcba_prefix_planned.tla_build_order_id
      where mrp.kodiak_pcba_prefix_planned.pcba_prefix = $1`,
    [pcbaPrefix],
  );

  const rows: KodiakPlannedPrefix[] = result.rows.map((row) => ({
    pcba_prefix: String(row.pcba_prefix ?? ""),
    core_feature: String(row.core_feature ?? ""),
    expected_tla_sku: String(row.expected_tla_sku ?? ""),
    purchase_order_id: null,
    pcba_build_order_id: null,
    tla_build_order_id:
      row.tla_build_order_id == null ? null : Number(row.tla_build_order_id),
    tla_work_order_number:
      row.tla_work_order_number == null || String(row.tla_work_order_number).trim() === ""
        ? null
        : String(row.tla_work_order_number),
    po_number: null,
    notes: row.notes == null || String(row.notes).trim() === "" ? null : String(row.notes),
    allowed_tla_skus: parsePgTextArray(row.allowed_tla_skus),
  }));

  const picked = pickPlannedRowForRequiredTla(rows, input.required_tla_sku);
  if (picked == null) return null;

  return {
    pcba_prefix: picked.pcba_prefix,
    core_feature: picked.core_feature,
    expected_tla_sku: picked.expected_tla_sku,
    tla_build_order_id: picked.tla_build_order_id,
    tla_work_order_number: picked.tla_work_order_number,
    source: "planned",
    notes: picked.notes,
    allowed_tla_skus: picked.allowed_tla_skus,
  };
}

function tlaSkuHasBabaPrefix(tlaSku: string): boolean {
  return isBabaTlaSku(tlaSku);
}

/** True when the TLA component SKU is a BABA variant (for example BABA-FI-5A-W-TLA). */
export function isBabaTlaSku(tlaSku: string): boolean {
  return /^BABA-/i.test(tlaSku.trim());
}

function expectationIsBabaLocked(expectation: KodiakPcbaPrefixTlaExpectation): boolean {
  const allowed = (expectation.allowed_tla_skus ?? []).filter((sku) => sku.trim() !== "");
  if (allowed.length > 0) {
    return allowed.some((sku) => tlaSkuHasBabaPrefix(sku));
  }
  return tlaSkuHasBabaPrefix(expectation.expected_tla_sku);
}

export function kodiakPcbaPrefixTlaMismatchMessage(
  expectation: KodiakPcbaPrefixTlaExpectation,
  requiredTlaSku: string,
): string {
  if (expectation.source === "serial_exception") {
    return (
      `This PCBA serial is assigned to ${expectation.expected_tla_sku}; ` +
      `this job requires ${requiredTlaSku}.`
    );
  }

  const jobRequiresBaba = tlaSkuHasBabaPrefix(requiredTlaSku);
  const lockIsBaba = expectationIsBabaLocked(expectation);

  if (jobRequiresBaba && !lockIsBaba) {
    return `This PCBA batch is assigned to non-BABA TLA; this job requires ${requiredTlaSku}.`;
  }
  if (!jobRequiresBaba && lockIsBaba) {
    return `This PCBA batch is assigned to BABA TLA; this job requires ${requiredTlaSku}.`;
  }

  return `This PCBA batch is not assigned to the TLA required for this job; this job requires ${requiredTlaSku}.`;
}

export function kodiakPcbaPrefixMatchesRequiredTla(
  expectation: KodiakPcbaPrefixTlaExpectation,
  requiredTlaSku: string,
): boolean {
  const allowed = expectation.allowed_tla_skus ?? [];
  if (allowed.length > 0) {
    return allowed.some((sku) => kodiakTlaSkusEquivalent(sku, requiredTlaSku));
  }
  if (expectation.expected_tla_sku.trim().endsWith("-TLA-?")) {
    return kodiakCoreFeatureFromTlaSku(requiredTlaSku) === expectation.core_feature;
  }
  return kodiakTlaSkusEquivalent(expectation.expected_tla_sku, requiredTlaSku);
}
